import TreatmentPlanService, {
  type TTreatmentPlan,
  type TTreatmentPlanQueryParams,
  type TTreatmentDefaultOptions,
  type IListOfAvailableCooperatePracticesFilter,
  type IListOfAvailableCooperateStaffFilter,
  type TAddCooperateStaffParams,
} from "@/api/models/treatmentPlan/treatmentPlan";
import type { TPracticeStaff } from "@/api/models/practice/practiceStaff";
import { ETreatmentPlanJobState, getJobSignalSubType } from "@/api/models/treatmentPlan/job";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import { ref, type MaybeRefOrGetter } from "vue";
import dagre from "@dagrejs/dagre";
import { MarkerType, useVueFlow } from "@vue-flow/core";
import { EPracticeStaffTypeShort } from "@/api/models/practice/practiceStaff";
import { truncateString } from "@/utils/Formatters";
import { useSnackStore } from "@/stores/snackStore";
import { triggerDownload } from "@/composables/shared/useFile";
import {
  useGenericListQuery,
  useGenericDetailQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
} from "@/composables/shared/queries";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";

export const treatmentPlanJobsPermissionsChips = {
  "job:is_treatment_coordinator": {
    title: EPracticeStaffTypeShort.TREATMENT_COORDINATOR,
    color: "orange",
  },
  "job:is_orthodontist": {
    title: EPracticeStaffTypeShort.ORTHODONTIST,
    color: "deep-purple",
  },
  "job:is_gp": {
    title: EPracticeStaffTypeShort.GP,
    color: "blue-darken-2",
  },
  "job:is_gp_office_manager": {
    title: EPracticeStaffTypeShort.GP_OFFICE_MANAGER,
    color: "blue",
  },
  "job:is_ortho_practice_assistant": {
    title: EPracticeStaffTypeShort.ORTHO_PRACTICE_ASSISTANT,
    color: "deep-purple-lighten-2",
  },
  "job:is_gp_practice_assistant": {
    title: EPracticeStaffTypeShort.GP_PRACTICE_ASSISTANT,
    color: "blue-lighten-2",
  },
  "job:is_hygienist": {
    title: EPracticeStaffTypeShort.HYGIENIST,
    color: "success",
  },
  "job:is_ortho_office_manager": {
    title: EPracticeStaffTypeShort.ORTHO_OFFICE_MANAGER,
    color: "deep-purple-darken-3",
  },
};

export const TreatmentPlanQueryKeys = createQueryKeys(TreatmentPlanService.queryKey, (key) => ({
  list: key<TTreatmentPlanQueryParams>("list"),
  detail: key<string>("detail"),
  defaultForPatient: key<TTreatmentDefaultOptions>("defaultForPatient"),
  listOfAvailableCooperatePractices: key<IListOfAvailableCooperatePracticesFilter>(
    "listOfAvailableCooperatePractices"
  ),
  listOfAvailableCooperateStaff: key<IListOfAvailableCooperateStaffFilter>(
    "listOfAvailableCooperateStaff"
  ),
  actionableCounts: () => [...TreatmentPlanQueryKeys.all(), "actionable-counts"] as const,
}));

export const useTreatmentPlans = (
  filters: MaybeRefOrGetter<Partial<TTreatmentPlanQueryParams>>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TTreatmentPlan, Partial<TTreatmentPlanQueryParams>>({
    queryKey: TreatmentPlanQueryKeys.list(),
    params: { filters, enabled },
    fetcher: (filters) => TreatmentPlanService.getAll({}, filters),
    permission: $permSubjects.TREATMENT_PLAN__TREATMENTPLAN,
  });

export const useTreatmentPlan = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery({
    queryKey: TreatmentPlanQueryKeys.detail(),
    fetcher: (id) => TreatmentPlanService.get({ id }),
    params,
    permission: $permSubjects.TREATMENT_PLAN__TREATMENTPLAN,
    ...options,
  });
};

export const useTreatmentPlanActionableCounts = (options: TItemQueryBehaviorOptions = {}) => {
  return useGenericQuery({
    queryKey: TreatmentPlanQueryKeys.actionableCounts(),
    fetcher: () => TreatmentPlanService.actionableCounts({}),
    params: {
      id: "actionable-counts",
    },
    permission: $permSubjects.TREATMENT_PLAN__TREATMENTPLAN,
    ...options,
  });
};

/**
 * The `patient_treatment_plan` states that split a treatment-plan dashboard into its
 * Archived tab and everything else. A plan is archived once it has stopped moving —
 * `completed` or `failed`; every other tab shows the plans still running.
 *
 * The backend requires this explicitly on both sides: omitting `state` returns archived
 * and active plans mixed together, so each tab of a TP dashboard sends one of these.
 * Shared by the referral and co-treatment dashboards, which archive identically.
 */
export const ARCHIVED_PLAN_STATES: ETreatmentPlanJobState[] = [
  ETreatmentPlanJobState.FAILED,
  ETreatmentPlanJobState.COMPLETED,
];

export const ACTIVE_PLAN_STATES: ETreatmentPlanJobState[] = [
  ETreatmentPlanJobState.PENDING,
  ETreatmentPlanJobState.IN_PROGRESS,
];

export const useCreateTreatmentPlan = () => {
  return useGenericMutation<TTreatmentPlan, Partial<TTreatmentPlan>>({
    mutationFn: (data) => TreatmentPlanService.create({}, data),
    feature: "treatmentPlan.create",
  });
};

export const useUpdateTreatmentPlan = () => {
  return useGenericMutation<TTreatmentPlan, Partial<TTreatmentPlan> & { id: string }>({
    mutationFn: (data) => TreatmentPlanService.update({ id: data.id }, data),
    feature: "treatmentPlan.update",
  });
};

export const useDefaultTreatmentPlanForPatient = (
  filters: MaybeRefOrGetter<TTreatmentDefaultOptions | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericDetailQuery<TTreatmentPlan, TTreatmentDefaultOptions | undefined>({
    queryKey: TreatmentPlanQueryKeys.defaultForPatient(),
    params: { filters, enabled },
    fetcher: (options) => TreatmentPlanService.defaultForPatient(options!),
    neededKey: ["patientId", "treatmentType"],
    suppressErrors: true,
  });

export const useDuplicateTreatmentPlan = () => {
  return useGenericMutation<TTreatmentPlan, { id: string; name: string }>({
    mutationFn: (data) => TreatmentPlanService.duplicate({ id: data.id }, { name: data.name }),
    feature: "treatmentPlan.duplicate",
  });
};

export const useDownloadTreatmentPlan = () => {
  const snackStore = useSnackStore();
  const isDownloading = ref(false);

  const downloadTreatmentPlan = async (planId: string) => {
    try {
      isDownloading.value = true;

      const {
        data: { data },
      } = await TreatmentPlanService.export({ id: planId });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });

      triggerDownload(blob, `treatment plan ${data.name}.json`);
    } catch (error) {
      snackStore.showError(error);
    } finally {
      isDownloading.value = false;
    }
  };

  return { downloadTreatmentPlan, isDownloading };
};

export const convertPlanBlobToJson = async (file: File): Promise<TTreatmentPlan> => {
  try {
    const text = await file.text();
    return JSON.parse(text) as TTreatmentPlan;
  } catch {
    throw new Error("Invalid treatment plan file");
  }
};

export const useImportTreatmentPlan = () => {
  const snackStore = useSnackStore();
  const isImporting = ref(false);

  async function importTreatmentPlan(
    planId: string,
    source: File | TTreatmentPlan,
    applyChanges = false
  ) {
    try {
      isImporting.value = true;

      const dataToImport = source instanceof File ? await convertPlanBlobToJson(source) : source;

      const { data } = await TreatmentPlanService.import(
        { id: planId },
        {
          data: dataToImport,
          apply_changes: applyChanges,
        }
      );

      return data;
    } catch (error) {
      snackStore.showError(error);
      throw error;
    } finally {
      isImporting.value = false;
    }
  }

  return { importTreatmentPlan, isImporting };
};
export const GRAPH_CONSTANTS = {
  DEFAULT_NODE_WIDTH: 200,
  DEFAULT_NODE_HEIGHT: 40,
  LAYOUT_DELAY: 250,
  DEFAULT_NODE_TYPE: "input",
  DEFAULT_EDGE_TYPE: "default",
  BACKLOOP_EDGE_TYPE: "backloop",
} as const;

export interface GraphNode {
  id: string;
  type: string;
  class: string;
  label: string;
  dependencies: string[];
  position: { x: number; y: number };
  dimensions: {
    width: number;
    height: number;
  };
  data: {
    state: string;
    completed_at: Date | null;
    name: string;
    meta_data: string | null | object;
    type: string;
    subType?: string;
    phase: number;
    operator: string;
    exclusion_jobs: string[];
    permissions: number[];
  };
}

export type GraphNodeData = GraphNode["data"];

export interface GraphEdge {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  type: string;
  animated: boolean;
  class: string;
  markerEnd?:
    | string
    | {
        type: MarkerType;
        color?: string;
      };
  markerMid?:
    | string
    | {
        type: MarkerType;
        color?: string;
      };
  label?: string;
  labelStyle?: {
    fill?: string;
    fontWeight?: string;
    fontSize?: string;
  };
  labelShowBg?: boolean;
  labelBgStyle?: {
    fill?: string;
    fillOpacity?: number;
  };
  style: {
    strokeDasharray?: string | null;
    strokeWidth: number;
    stroke?: string;
  };
  data?: {
    isBackloop?: boolean;
  };
}

export type LayoutDirection = "LR" | "TB" | "BT";

export function createNodesFromTreatmentPlan(
  treatmentPlan: TTreatmentPlan | null,
  getPositionFromStore?: (jobId: string) => { x: number; y: number } | null
): GraphNode[] {
  if (!treatmentPlan || !treatmentPlan.jobs) return [];

  return treatmentPlan.jobs
    .filter((job) => job.reference_id)
    .map((job) => {
      let position = { x: 0, y: 0 };

      const jobWithMeta = job as { _?: unknown; meta_data?: unknown };
      type TMetadata = { graph?: { position?: { x?: number; y?: number } } };
      let metadata: TMetadata | null = null;
      if (jobWithMeta._ && typeof jobWithMeta._ === "object" && jobWithMeta._ !== null) {
        metadata = jobWithMeta._ as TMetadata;
      } else if (
        jobWithMeta.meta_data &&
        typeof jobWithMeta.meta_data === "object" &&
        jobWithMeta.meta_data !== null
      ) {
        metadata = jobWithMeta.meta_data as TMetadata;
      }

      const hasMetadata = metadata !== null;
      const metadataPosition = metadata?.graph?.position;
      const hasMetadataPosition = metadataPosition?.x != null && metadataPosition.y != null;

      if (
        hasMetadataPosition &&
        metadataPosition &&
        metadataPosition.x != null &&
        metadataPosition.y != null
      ) {
        position = {
          x: metadataPosition.x,
          y: metadataPosition.y,
        };
      } else if (!hasMetadata && getPositionFromStore) {
        const storePosition = getPositionFromStore(job.reference_id);
        if (storePosition) {
          position = storePosition;
        }
      }
      return {
        id: job.reference_id,
        type: GRAPH_CONSTANTS.DEFAULT_NODE_TYPE,
        class: job.state ?? job.signal_type,
        label: job.name,
        dependencies: job.depends_on ?? [],
        position,
        dimensions: {
          width: 250,
          height: 50,
        },
        data: {
          state: job.state,
          completed_at: job.completed_at ? new Date(job.completed_at) : null,
          name: job.name,
          meta_data: job.meta_data ?? null,
          type: job.signal_type ?? "system",
          subType: getJobSignalSubType(job),
          permissions: job.required_read_permissions,
          operator: job.depends_on_operator ?? "and",
          phase: job.phase ?? 0,
          exclusion_jobs: job.exclusion_jobs ?? [],
        },
      };
    });
}

export function createEdgesFromNodes(
  nodes: GraphNode[],
  treatmentPlan?: { back_loops?: { from_job: string; to_job: string }[] } | string
): GraphEdge[] {
  const edges = createTreeEdges(nodes);
  if (typeof treatmentPlan === "object" && treatmentPlan?.back_loops) {
    const backloopEdges = createBackloopEdges(nodes, treatmentPlan.back_loops);
    return [...edges, ...backloopEdges];
  }
  return edges;
}

export function createTreeEdges(nodes: GraphNode[]): GraphEdge[] {
  const edges: GraphEdge[] = [];

  for (const node of nodes) {
    for (const dependency of node.dependencies) {
      const targetNode = nodes.find((n) => n.id === dependency);
      if (targetNode) {
        const operator = node.data.operator;
        let strokeDasharray: string | null = null;
        let edgeClass = "edge-and";

        if (operator === "or") {
          edgeClass = "edge-or";
          strokeDasharray = "8, 4";
        }

        edges.push({
          id: `${dependency}-${node.id}`,
          source: dependency,
          sourceHandle: "bottom",
          target: node.id,
          targetHandle: "top",
          type: GRAPH_CONSTANTS.DEFAULT_EDGE_TYPE,
          animated: false,
          class: edgeClass,
          style: {
            strokeDasharray,
            strokeWidth: 2,
          },
        });
      }
    }
  }

  return edges;
}

export function createBackloopEdges(
  nodes: GraphNode[],
  backloops?: Array<{ from_job: string; to_job: string; name?: string }>
): GraphEdge[] {
  if (!backloops || backloops.length === 0) {
    return [];
  }

  const edges: GraphEdge[] = [];

  for (const backloop of backloops) {
    const fromNode = nodes.find((n) => n.id === backloop.from_job);
    const toNode = nodes.find((n) => n.id === backloop.to_job);

    if (fromNode && toNode) {
      const backloopEdge: GraphEdge = {
        id: `backloop-${backloop.from_job}-${backloop.to_job}`,
        source: backloop.from_job,
        sourceHandle: "bottom",
        target: backloop.to_job,
        targetHandle: "top",
        type: GRAPH_CONSTANTS.BACKLOOP_EDGE_TYPE,
        animated: false,
        class: "edge-backloop",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#6b21a8",
        },
        markerMid: {
          type: MarkerType.ArrowClosed,
          color: "#6b21a8",
        },
        data: { isBackloop: true },
        style: {
          strokeWidth: 2,
          stroke: "#6b21a8",
        },
      };

      if (backloop.name) {
        backloopEdge.label = truncateString(backloop.name, 25);
        backloopEdge.labelStyle = {
          fill: "#ffffff",
          fontWeight: "600",
          fontSize: "1.25rem",
        };
        backloopEdge.labelShowBg = true;
        backloopEdge.labelBgStyle = {
          fill: "#6b21a8",
          fillOpacity: 0.9,
        };
      }

      edges.push(backloopEdge);
    }
  }

  return edges;
}

export const useListOfAvailableCooperatePractices = (
  filters: MaybeRefOrGetter<IListOfAvailableCooperatePracticesFilter>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<string, IListOfAvailableCooperatePracticesFilter>({
    queryKey: TreatmentPlanQueryKeys.listOfAvailableCooperatePractices(),
    params: { filters, enabled },
    fetcher: (f) =>
      TreatmentPlanService.listOfAvailableCooperatePractices({
        treamentPlanId: f!.treamentPlanId!,
        practiceType: f!.practiceType!,
      }),
    justQueryKey: true,
  });

export const useListOfAvailableCooperateStaff = (
  filters: MaybeRefOrGetter<IListOfAvailableCooperateStaffFilter>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TPracticeStaff, IListOfAvailableCooperateStaffFilter>({
    queryKey: TreatmentPlanQueryKeys.listOfAvailableCooperateStaff(),
    params: { filters, enabled },
    fetcher: (f) =>
      TreatmentPlanService.listOfAvailableCooperateStaff({
        treamentPlanId: f!.treamentPlanId!,
        staffType: f!.staffType!,
      }),
    neededKey: ["treamentPlanId", "staffType"],
  });

export const useAddCooperateStaff = () =>
  useGenericMutation<void, TAddCooperateStaffParams>({
    mutationFn: ({ treamentPlanId, staffType, jobId, staff }) =>
      TreatmentPlanService.addCooperateStaff(
        { treamentPlanId, staffType },
        { staff },
        { job_id: jobId }
      ),
    feature: "treatmentPlan.addCooperateStaff",
    params: { neededKey: ["treamentPlanId", "staffType", "jobId", "staff"] },
  });

export const useTreatmentPlanOfPatient = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery({
    queryKey: TreatmentPlanQueryKeys.detail(),
    fetcher: (id) => TreatmentPlanService.getOfPatient({ id }),
    params,
    ...options,
    suppressErrors: [404],
  });
};

export function useLayout() {
  const { findNode } = useVueFlow();
  const graph = ref(new dagre.graphlib.Graph());
  const previousDirection = ref<LayoutDirection>("LR");

  function layout(
    nodes: GraphNode[],
    edges: GraphEdge[],
    direction: LayoutDirection,
    nodeSpacing: number,
    rankSpacing: number
  ): GraphNode[] {
    const dagreGraph = new dagre.graphlib.Graph();
    graph.value = dagreGraph;

    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
      rankdir: direction,
      nodesep: nodeSpacing,
      edgesep: nodeSpacing,
      ranksep: rankSpacing,
    });
    previousDirection.value = direction;

    for (const node of nodes) {
      const graphNode = findNode(node.id);
      dagreGraph.setNode(node.id, {
        width:
          graphNode && graphNode.dimensions
            ? graphNode.dimensions.width
            : GRAPH_CONSTANTS.DEFAULT_NODE_WIDTH,
        height:
          graphNode && graphNode.dimensions
            ? graphNode.dimensions.height
            : GRAPH_CONSTANTS.DEFAULT_NODE_HEIGHT,
      });
    }

    for (const edge of edges) {
      dagreGraph.setEdge(edge.source, edge.target);
    }

    dagre.layout(dagreGraph);

    return nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);

      return {
        ...node,
        position: { x: nodeWithPosition.x, y: nodeWithPosition.y },
      };
    });
  }

  return {
    graph,
    layout,
    previousDirection,
  };
}
